import { Grid, Stack, Typography } from "@mui/material";
import { DateRangeSelector } from "components/common/DateRangeSelector";
import { useDateRange } from "contexts/DateRangeContext";
import { useTenantTranslation } from "hooks/formatters";
import { useFormatLocalDate } from "hooks/useFormatLocalDate";
import { useMemo } from "react";
import { TimeAggregation } from "src/store/api/generatedApi";
import { GroupedScheduleSeries } from "../types";
import { useStringQueryParamState } from "hooks/useStringQueryParamState";
import { useLiteralListQueryParamState } from "hooks/useLiteralListQueryParamState";
import { useTenant } from "hooks/settings";
import { MultiSelect } from "../../common/inputs/multiSelect";
import { ToggleSelector } from "../ToggleSelector";
import { SchedulePlot } from "./SchedulePlot";
import { ScheduleTable } from "./ScheduleTable";

const SCHEDULE_ZOOM_PRESETS = [
  { id: "1week", duration: "1 week" as const },
  { id: "1month", duration: "1 month" as const },
  { id: "3months", duration: "3 months" as const },
  { id: "6months", duration: "6 months" as const },
];

interface ScheduleTabProps {
  groupedScheduleSeries: GroupedScheduleSeries;
}

export const ScheduleTab = ({ groupedScheduleSeries }: ScheduleTabProps) => {
  const { t } = useTenantTranslation();
  const tenant = useTenant();
  const { startDate, endDate } = useDateRange();
  const formatDate = useFormatLocalDate("date", "UTC");

  const [selectedTimeAggregation, setSelectedTimeAggregation] =
    useStringQueryParamState<TimeAggregation | null>(
      "timeAggregation",
      getDefaultTimeAggregation(groupedScheduleSeries.availableTimeAggregations)
    );

  const [selectedMixGroups, setSelectedMixGroups] =
    useLiteralListQueryParamState<string>({
      paramName: "mixGroups",
      permitted: groupedScheduleSeries.availableMixGroups,
      localStorageKey: `${tenant}.strategyDashboard.schedule.selectedMixGroups`,
      defaultValue: groupedScheduleSeries.availableMixGroups,
    });

  const availableTimeAggregationLabels = useMemo(
    () =>
      Object.fromEntries(
        Array.from(groupedScheduleSeries.availableTimeAggregations).map(
          (aggregation) => [aggregation, aggregation]
        )
      ) as Record<TimeAggregation, string>,
    [groupedScheduleSeries]
  );

  return (
    <div>
      <Stack
        direction="row"
        spacing={2}
        justifyContent="space-between"
        sx={{ mt: 2 }}
      >
        <DateRangeSelector
          direction="forward"
          zoomPresets={SCHEDULE_ZOOM_PRESETS}
        />
      </Stack>
      <Stack direction="row" spacing={2} sx={{ alignItems: "flex-end", mt: 1 }}>
        {selectedTimeAggregation === null ? null : (
          <ToggleSelector<TimeAggregation>
            title={"frequency"}
            selectedValue={selectedTimeAggregation}
            setSelectedValue={setSelectedTimeAggregation}
            availableValueLabels={availableTimeAggregationLabels}
          />
        )}
        <MultiSelect<string, string>
          sx={{ minWidth: 240 }}
          options={groupedScheduleSeries.availableMixGroups}
          getOptionId={(mixGroup) => mixGroup}
          getOptionLabel={(mixGroup) => t(mixGroup)}
          values={selectedMixGroups}
          setValues={setSelectedMixGroups}
          small
          selectAll
          labelAbove
          label={t("mixGroups")}
        />
      </Stack>
      <Grid container spacing={1} mt={2} mx={0}>
        <Grid size={{ xs: 8 }} ml={0}>
          {selectedTimeAggregation === null ? null : (
            <SchedulePlot
              scheduleSeries={groupedScheduleSeries}
              selectedTimeAggregation={selectedTimeAggregation}
              selectedMixGroups={selectedMixGroups}
            />
          )}
        </Grid>
        <Grid size={{ xs: 4 }}>
          <Typography variant="h5">
            {`${t("scheduleDuring")} ${formatDate(startDate.toDate())} – ${formatDate(endDate.toDate())}`}
          </Typography>
          <Typography variant="body1">
            {t("scheduleTableSubtitleText")}
          </Typography>
          {selectedTimeAggregation === null ? null : (
            <ScheduleTable
              scheduleSeries={groupedScheduleSeries}
              selectedTimeAggregation={selectedTimeAggregation}
            />
          )}
        </Grid>
      </Grid>
    </div>
  );
};

const getDefaultTimeAggregation = (
  availableTimeAggregations: Set<TimeAggregation>
): TimeAggregation | null => {
  if (availableTimeAggregations.has("daily")) return "daily";
  return Array.from(availableTimeAggregations)[0] ?? null;
};
